import { FC, ReactNode, useCallback, useMemo } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRatesExpansion } from "../../../RatesExpansion/RatesExpansionContext";
import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { RateIdleHeader } from "../RateIdleHeader/RateIdleHeader";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DeleteConfirmations } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { getRatesDefaultInformations } from "@/features/LiveFormEditor/Steps/StepTickets/stepTickets.helper";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Copy as DuplicateIcon,
  Delete as DeleteIcon,
  Move as DragIcon,
} from "@/icons/outlined";

interface RateCardShellProps {
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  /** Per-attendee questions the API will archive with this rate. Non-empty forces the delete confirmation
   * and names them in it, so the organizer decides before the card is removed from the form. */
  questionsArchivedOnDelete?: string[];
  extraActions?: ReactNode;
  visibilityAction?: ReactNode;
  statusChip?: ReactNode;
  duplicateTooltip?: string;
  deleteTooltip?: string;
  isDeleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  fallbackName?: string;
  hideIndexInTitle?: boolean;
  children: ReactNode;
}

export const COLLAPSIBLE_RATE_CATEGORIES: ReadonlySet<TicketingFormCategory> = new Set([
  TicketingFormCategory.Auction,
  TicketingFormCategory.Shop,
  TicketingFormCategory.Custom,
]);

export const RateCardShell: FC<RateCardShellProps> = ({
  totalCardNumber,
  shouldDisplayDeleteButton: shouldDisplayDeleteButtonProp,
  shouldConfirmDelete: shouldConfirmDeleteProp,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  questionsArchivedOnDelete,
  extraActions,
  visibilityAction,
  statusChip,
  duplicateTooltip,
  deleteTooltip,
  isDeleteDisabled = false,
  deleteDisabledTooltip,
  fallbackName: fallbackNameProp,
  hideIndexInTitle = false,
  children,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { rateId, rateIndex, formCategory, alreadyBoughtTicketsCount, isSweepstakes } = useRateContext();
  const { control, register } = useFormContext<StepTicketsFormData>();
  const { expandedRateId, toggleExpanded: toggleSharedExpansion } = useRatesExpansion();

  const idField = useRateFieldName("id");
  const nameField = useRateFieldName("name");
  const descriptionField = useRateFieldName("description");
  const amountField = useRateFieldName("amount");
  const typedName = useWatch({ control, name: nameField });
  const typedDescription = useWatch({ control, name: descriptionField });
  const typedAmount = useWatch({ control, name: amountField });

  const isCollapsible = COLLAPSIBLE_RATE_CATEGORIES.has(formCategory);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rateId });

  const isOpen = expandedRateId === rateId;
  const isExpanded = isCollapsible ? isOpen : true;
  const showActionButtons = !isCollapsible || isExpanded;

  const handleDelete = useCallback(() => onDelete(rateIndex), [onDelete, rateIndex]);
  const handleDuplicate = useCallback(() => onDuplicate(rateIndex), [onDuplicate, rateIndex]);
  const handleMoveUp = useCallback(() => onMoveUp(rateIndex), [onMoveUp, rateIndex]);
  const handleMoveDown = useCallback(() => onMoveDown(rateIndex), [onMoveDown, rateIndex]);
  const toggleCardExpanded = useCallback(() => toggleSharedExpansion(rateId), [toggleSharedExpansion, rateId]);

  const hasMultipleCards = totalCardNumber > 1;
  const canDrag = hasMultipleCards && !isSmallScreen;
  const showMobileArrows = isSmallScreen && hasMultipleCards;
  const canMoveUp = rateIndex > 0;
  const canMoveDown = rateIndex < totalCardNumber - 1;

  const defaultInformations = useMemo(
    () =>
      getRatesDefaultInformations({
        formCategory,
        t,
      }),
    [formCategory, t]
  );
  const isShopWithSales = formCategory === TicketingFormCategory.Shop && alreadyBoughtTicketsCount > 0;
  const shouldDisplayDeleteButton = shouldDisplayDeleteButtonProp ?? (hasMultipleCards && !isShopWithSales);

  const isDefaultTitle =
    !typedName?.trim() || typedName === defaultInformations.rate1Title || typedName === defaultInformations.rate2Title;
  const isDefaultDescription =
    !typedDescription?.trim() ||
    typedDescription === defaultInformations.rate1Description ||
    typedDescription === defaultInformations.rate2Description;
  const isDefaultPrice = !typedAmount;
  // A rate that takes questions with it is always confirmed, however untouched the card looks, and its
  // confirmation replaces the generic one rather than adding a second step.
  const archivedQuestions = questionsArchivedOnDelete ?? [];
  const shouldConfirmDelete =
    archivedQuestions.length > 0 ||
    (shouldConfirmDeleteProp ?? (!isDefaultTitle || !isDefaultDescription || !isDefaultPrice));
  const isShop =
    formCategory === TicketingFormCategory.Shop ||
    formCategory === TicketingFormCategory.Auction ||
    formCategory === TicketingFormCategory.Custom;
  const deleteConfirmationMessages =
    archivedQuestions.length > 0
      ? {
          title: t("dashboard", "common.areYouSure"),
          description: isSweepstakes
            ? t("dashboard", "sweepstakes.rates.deleteArchivesQuestions", {
                count: archivedQuestions.length,
                questions: archivedQuestions.join(", "),
              })
            : t(
                "dashboard",
                isShop
                  ? "ticketing.deleteRateArchivesQuestions.bodyItem"
                  : "ticketing.deleteRateArchivesQuestions.body",
                { count: archivedQuestions.length, questions: archivedQuestions.join(", ") }
              ),
          cancel: t("dashboard", "common.cancel"),
          confirm: t("dashboard", "common.delete"),
        }
      : undefined;

  const trimmedName = typedName?.trim() ?? "";
  const fallbackName =
    fallbackNameProp ??
    (formCategory === TicketingFormCategory.MembershipV2
      ? t("ticketing", "membershipRate")
      : formCategory === TicketingFormCategory.Auction ||
          formCategory === TicketingFormCategory.Shop ||
          formCategory === TicketingFormCategory.Custom
        ? t("ticketing", "itemRate")
        : t("ticketing", "ticketRate"));
  const cardTitle = trimmedName || fallbackName;
  const titleWithIndex = hideIndexInTitle ? cardTitle : `${rateIndex + 1}. ${cardTitle}`;

  return (
    <Box
      ref={setNodeRef}
      data-index={rateIndex}
      {...attributes}
      sx={{
        transform: CSS.Translate.toString(transform),
        transition,
        boxShadow: isDragging ? "0 4px 8px rgba(0,0,0,0.2)" : undefined,
        zIndex: isDragging ? 1000 : undefined,
        position: "relative",
      }}
    >
      <Paper
        sx={(theme) => ({
          paddingY: 2.5,
          paddingX: 3,
          backgroundColor: theme.palette.surface.neutral.supershy,
          borderRadius: theme.radius(1),
          boxShadow: "none",
        })}
      >
        <Stack spacing={isExpanded ? 1 : 0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {canDrag && (
              <Box
                {...listeners}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "grab",
                  flexShrink: 0,
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <DashboardIconButton vibe="neutral" size="small" sx={{ cursor: "inherit" }}>
                  <DragIcon />
                </DashboardIconButton>
              </Box>
            )}

            {isCollapsible ? (
              <Box
                onClick={toggleCardExpanded}
                sx={{ display: "flex", flex: 1, minWidth: 0, cursor: "pointer" }}
                data-test="card-header-toggle"
              >
                <RateIdleHeader title={titleWithIndex} isExpanded={isExpanded} />
              </Box>
            ) : (
              <RateIdleHeader title={titleWithIndex} isExpanded={isExpanded} />
            )}

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              {statusChip}

              {showMobileArrows && (
                <>
                  <DashboardIconButton
                    size="small"
                    variant="text"
                    vibe="neutral"
                    onClick={handleMoveUp}
                    disabled={!canMoveUp}
                    data-test="card-move-up-button"
                  >
                    <ArrowUp />
                  </DashboardIconButton>
                  <DashboardIconButton
                    size="small"
                    variant="text"
                    vibe="neutral"
                    onClick={handleMoveDown}
                    disabled={!canMoveDown}
                    data-test="card-move-down-button"
                  >
                    <ArrowDown />
                  </DashboardIconButton>
                </>
              )}

              {showActionButtons && (
                <>
                  {extraActions}

                  <DashboardTooltip title={duplicateTooltip} disabled={!duplicateTooltip}>
                    <DashboardIconButton
                      onClick={handleDuplicate}
                      size="small"
                      vibe="neutral"
                      title={duplicateTooltip ? undefined : t("common", "duplicate")}
                      data-test="card-duplicate-button"
                    >
                      <DuplicateIcon />
                    </DashboardIconButton>
                  </DashboardTooltip>

                  {visibilityAction}

                  {shouldDisplayDeleteButton && isDeleteDisabled && (
                    <DashboardTooltip title={deleteDisabledTooltip} disabled={!deleteDisabledTooltip}>
                      <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                        <DashboardIconButton
                          size="small"
                          vibe="neutral"
                          disabled
                          data-test="card-delete-disabled-button"
                        >
                          <DeleteIcon />
                        </DashboardIconButton>
                      </Box>
                    </DashboardTooltip>
                  )}

                  {shouldDisplayDeleteButton && !isDeleteDisabled && (
                    <DashboardTooltip title={deleteTooltip} disabled={!deleteTooltip}>
                      <Box sx={{ flexShrink: 0 }}>
                        <DeleteConfirmations
                          confirmation={handleDelete}
                          shouldConfirm={shouldConfirmDelete}
                          customMessages={deleteConfirmationMessages}
                        />
                      </Box>
                    </DashboardTooltip>
                  )}
                </>
              )}

              {isCollapsible && (
                <DashboardIconButton
                  onClick={toggleCardExpanded}
                  size="small"
                  vibe="neutral"
                  title={t("common", isExpanded ? "collapse" : "expand")}
                  data-test="card-expand-toggle"
                >
                  {isExpanded ? <ChevronUp /> : <ChevronDown />}
                </DashboardIconButton>
              )}
            </Stack>
          </Stack>

          {isCollapsible ? (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
              <Box
                sx={(theme) => ({
                  opacity: isExpanded ? 1 : 0,
                  transition: theme.transitions.create("opacity", {
                    duration: theme.transitions.duration.shorter,
                  }),
                  transitionDelay: isExpanded ? theme.transitions.duration.shortest + "ms" : "0ms",
                })}
              >
                {children}
              </Box>
            </Collapse>
          ) : (
            children
          )}
        </Stack>
      </Paper>
      <input style={{ display: "none" }} {...register(idField)} value={rateId} readOnly />
    </Box>
  );
};
